import {
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import CoreButton from "../../../../app/core/components/CoreButton/CoreButton";
import CoreInputField from "../../../../app/core/components/CoreInputField/CoreInputField";
import UserFilter from "../../../../app/core/components/UserFilter/UserFilter";
import {
  useCreatePracticeMutation,
  useGetPracticesServicesQuery,
} from "../../../api/admin/gestisciPraticheApi";
import { useGetUsersQuery } from "../../../api/admin/gestisciUtentiApi";
import { useGetCommercialistaQuery } from "../../../api/admin/gestisciCommercialistiApi";
import { TABLE_ROWS_NUMBER } from "../../../../utils/constants/tables";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useGetRolesQuery } from "../../../api/admin/rolesApi";
import { ROLE } from "../../../../utils/constants/profiles";

const CreaPratichePage = ({ closeModal }) => {
  const { t } = useTranslation();
  const appConfig = useSelector((state) => state.app.appConfig);
  const role = useSelector((state) => state.app.role);
  const studioId = useSelector((state) => state.app.studioId);
  const { primaryColor, secondaryColor } = useSelector((s) => s.app);
  const [selectedUser, setSelectedUser] = useState("");
  const [debouncedInputUserValue, setDebouncedInputUserValue] = useState("");
  const [debouncedInputOperatorValue, setDebouncedInputOperatorValue] =
    useState("");
  const [selectedService, setSelectedService] = useState("");
  const [showOtherInfo, setShowOtherInfo] = useState(false);
  const [selectedOperatorType, setSelectedOperatorType] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("");
  const [expirationDate, setExpirationDate] = useState();
  const [userData, setUserData] = useState({
    utente: "",
    nome: "",
  });
  const [page] = useState({
    currentPage: 1,
    startRow: 0,
    endRow: TABLE_ROWS_NUMBER,
  });
  const [formErrors, setFormErrors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [createPractice, { isLoading: isCreating }] =
    useCreatePracticeMutation();
  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useGetUsersQuery({
    page: { startRow: 0, endRow: 5 },
    value: debouncedInputUserValue,
  });
  const { data: adminRoles } = useGetRolesQuery({});

  const { data: serviceOptions } = useGetPracticesServicesQuery();

  const {
    data: operatorsData,
    isLoading: isOperatorLoading,
    isFetching: isOperatorFetching,
  } = useGetCommercialistaQuery({
    page,
    value: debouncedInputOperatorValue,
    filter: { identityRoleId: selectedOperatorType, studioId: "" },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newFormErrors = {
      utente: !selectedUser,
      servizio: !selectedService,
      nome: !userData.nome,
    };

    setFormErrors(newFormErrors);
    if (Object.values(newFormErrors).every((value) => !value)) {
      const newPracticeData = {
        userId: selectedUser.id,
        serviceId: selectedService,
        name: userData.nome,
        adminId: selectedOperator
          ? selectedOperator.id
          : usersData?.Data?.items[0]?.adminId,
      };
      if (expirationDate) {
        newPracticeData.expirationDate = expirationDate;
      }
      if (appConfig?.modules?.practice?.subtask) {
        newPracticeData.addTasks = true;
      }
      createPractice(newPracticeData).then((response) => {
        if (response?.data?.Result !== 1) {
          toast.error(t("PracticeNotCreated"));
          return;
        }
        toast.success(t("PracticeCreated"));
        closeModal();
      });
    }
  };

  // Controllo parametrizzato (serviceWithLLM)
  const filteredItems = appConfig?.modules?.services?.prompt
    ? serviceOptions?.Data?.items
    : serviceOptions?.Data?.items?.filter((el) => el.aiPrompt === null);

  return (
    <div style={{ textAlign: "center", marginTop: "50px" }}>
      <h2>{t("NewPractice")}</h2>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{ marginBottom: "20px", width: "70%", marginTop: "15px" }}
          className="flex gap-6 items-end"
        >
          <FormControl
            className="w-2/5"
            size="small"
            required
            error={formErrors.servizio}
          >
            <InputLabel id="servizio-label">{t("ChooseService")}</InputLabel>
            <Select
              className="text-left"
              label={t("ChooseService")}
              onChange={(event) => {
                setFormErrors((prev) => ({ ...prev, servizio: "" }));
                setSelectedService(event.target.value);
              }}
              value={selectedService}
            >
              {(role !== ROLE.ADMIN
                ? filteredItems?.filter(
                    (option) =>
                      option.activeStudios &&
                      option.activeStudios.some(
                        (studio) => studio.id === studioId
                      )
                  )
                : filteredItems
              )?.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.servizio && (
              <FormHelperText>{t("FieldRequired")}</FormHelperText>
            )}
          </FormControl>
          <CoreInputField
            className="w-3/5"
            label={`${t("Name")} ${t("practice")}`}
            name="nome"
            value={userData.nome}
            onChange={handleInputChange}
            fullWidth
            required
            error={formErrors.nome}
            helperText={formErrors.nome && t("FieldRequired")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreateRoundedIcon sx={{ color: "#5c5c5c" }} />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div
          style={{
            marginBottom: "20px",
            width: "70%",
            paddingTop: "30px",
          }}
        >
          <FormControl
            fullWidth
            size="small"
            required
            error={formErrors.utente}
          >
            <UserFilter
              selectedUser={selectedUser}
              setSelectedUser={(e) => {
                setSelectedUser(e);
                setFormErrors((prev) => ({ ...prev, utente: "" }));
              }}
              usersList={usersData?.Data?.items || []}
              setDebouncedInputUserValue={setDebouncedInputUserValue}
              isUsersLoading={isUsersLoading || isUsersFetching}
              isError={formErrors.utente}
              isRequired
              startAdornment={
                <PersonRoundedIcon style={{ color: "#5c5c5c" }} />
              }
            />

            {formErrors.utente && (
              <FormHelperText>{t("FieldRequired")}</FormHelperText>
            )}
          </FormControl>
        </div>

        <div
          style={{
            marginBottom: "20px",
            width: showOtherInfo ? "85%" : "70%",
            marginTop: "25px",
            margin: 4,
            padding: 20,
            border: "1px dashed lightgrey",
          }}
        >
          <div className="flex justify-center items-center gap-4">
            <Typography
              variant="h4"
              fontSize={18}
              fontWeight={600}
              color={showOtherInfo ? "black" : "grey"}
            >
              {t("OtherOptionalInfo")}
            </Typography>
            <IconButton
              size="small"
              className={`
                    ${
                      showOtherInfo
                        ? "text-black border-black bg-stone-100"
                        : "text-gray border-gray bg-stone-100"
                    }
                  `}
              onClick={() => setShowOtherInfo((prev) => !prev)}
            >
              {showOtherInfo ? (
                <KeyboardArrowDownRoundedIcon />
              ) : (
                <KeyboardArrowRightRoundedIcon />
              )}
            </IconButton>
          </div>
          {showOtherInfo && <Divider className="mt-4" />}
          {showOtherInfo && (
            <div className="flex items-center">
              <div className="w-2/3 flex flex-col items-center mt-8">
                <Typography
                  variant="h6"
                  fontSize={17}
                  fontWeight={600}
                  color="grey"
                >
                  {t("Operator")}
                </Typography>

                <div className="flex gap-6 w-full items-center mt-3 mb-4">
                  <FormControl size="large" className="w-14">
                    <Tooltip title={t("OperatorType")}>
                      <IconButton
                        style={{
                          color: selectedOperatorType
                            ? primaryColor
                            : "darkgray",
                          background: selectedOperatorType
                            ? secondaryColor + "50"
                            : "whitesmoke",
                        }}
                        size="large"
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                      >
                        <FilterAltRoundedIcon />
                      </IconButton>
                    </Tooltip>

                    <Popover
                      anchorEl={anchorEl}
                      open={!!anchorEl}
                      onClose={() => setAnchorEl(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      PaperProps={{
                        sx: { borderRadius: "8px", marginTop: "5px" },
                      }}
                    >
                      <MenuItem
                        value={""}
                        onClick={() => {
                          setSelectedOperatorType("");
                          setAnchorEl(null);
                        }}
                      >
                        <em>{t("All")}</em>
                      </MenuItem>
                      {adminRoles?.Data?.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          onClick={() => {
                            setSelectedOperatorType(option.id);
                            setAnchorEl(null);
                          }}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                    </Popover>
                  </FormControl>

                  <FormControl
                    className="w-full"
                    size="small"
                    required
                    error={formErrors.operatore}
                  >
                    <UserFilter
                      selectedUser={selectedOperator}
                      setSelectedUser={(e) => {
                        setSelectedOperator(e);
                        setFormErrors((prev) => ({
                          ...prev,
                          operatore: "",
                        }));
                      }}
                      usersList={operatorsData?.Data?.items || []}
                      setDebouncedInputUserValue={
                        setDebouncedInputOperatorValue
                      }
                      isUsersLoading={isOperatorLoading || isOperatorFetching}
                      externalLabel={`${t("BriefSearchOperator")}`}
                      externalPlaceholder={`${t("ExtSearchOperator")}`}
                      externalNegativeLabel={t("NoOperatorFound")}
                      startAdornment={
                        <AdminPanelSettingsRoundedIcon
                          style={{ color: "#5c5c5c" }}
                        />
                      }
                      isError={formErrors.operatore}
                    />
                    {formErrors.operatore && (
                      <FormHelperText>{t("FieldRequired")}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </div>

              <div className="w-1/3 flex flex-col items-center mt-8">
                <Typography
                  variant="h6"
                  fontSize={17}
                  fontWeight={600}
                  color="grey"
                >
                  {t("PracticeExpiration")}
                </Typography>
                <div className="mt-3 mb-4">
                  <CoreInputField
                    size="small"
                    variant="outlined"
                    avoidMt={true}
                    InputLabelProps={{ shrink: true }}
                    type="date"
                    name="expirationDate"
                    value={expirationDate}
                    onChange={(event) => {
                      setExpirationDate(event.target.value);
                    }}
                    fullWidth
                    error={formErrors.expirationDate}
                    helperText={formErrors.expirationDate && t("FieldRequired")}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: "40px" }}>
          <CoreButton
            disabled={isCreating}
            className={"btn-round"}
            variant="contained"
            testo={isCreating ? t("Wait") : t("Create")}
            onClick={handleSubmit}
          />
        </div>
      </form>
    </div>
  );
};

export default CreaPratichePage;
